import { Link } from "react-router-dom";
import type { WorkflowStepRendererProps } from "../types";
import type { ContentValidationSnapshot } from "../step-modules/content-validation";

export function ValidationStepRenderer({
  state,
}: WorkflowStepRendererProps<ContentValidationSnapshot>) {
  if (!state.runId) return null;

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-3 text-sm text-brand-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">Content validation</p>
          <p className="mt-1 text-xs text-brand-900/80">
            {state.isLoading
              ? "Loading validation run."
              : state.status === "running"
                ? "Checking live language, persona, and pain signals."
                : state.snapshot?.summary ?? "Validation run recorded."}
          </p>
        </div>
        <Link
          to={`/runs/${encodeURIComponent(state.runId)}`}
          className="text-xs font-medium text-brand-700 underline underline-offset-2"
        >
          View run
        </Link>
      </div>
      {(state.isLoading || state.status === "running") && (
        <div className="mt-3 flex items-center gap-2 text-xs text-brand-800">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          {state.isLoading ? "Loading validation run" : "Validation in progress"}
        </div>
      )}
      {state.error && !state.run && (
        <p className="mt-3 text-xs text-red-700">{state.error.message}</p>
      )}
      {state.run?.status === "failed" && (
        <p className="mt-3 text-xs text-red-700">
          {state.run.error ?? "Validation failed."}
        </p>
      )}
      {state.snapshot && state.run?.status === "completed" && (
        <div className="mt-3 space-y-2 text-xs text-brand-950">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-brand-700 ring-1 ring-inset ring-brand-200">
              Verdict: {state.snapshot.verdict}
            </span>
            {state.snapshot.personaLabel && (
              <span className="rounded-full bg-white px-2 py-0.5 font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
                Persona: {state.snapshot.personaLabel}
              </span>
            )}
          </div>
          {state.snapshot.painpoints.length > 0 && (
            <p><span className="font-semibold">Pain point:</span> {state.snapshot.painpoints.join(" · ")}</p>
          )}
          {state.snapshot.languageMatches.length > 0 && (
            <p><span className="font-semibold">Public language:</span> {state.snapshot.languageMatches.slice(0, 3).join(" · ")}</p>
          )}
          {state.snapshot.recommendedRewrite && (
            <p><span className="font-semibold">Recommended rewrite:</span> {state.snapshot.recommendedRewrite}</p>
          )}
          {state.snapshot.proofRequirements.length > 0 && (
            <div>
              <p className="font-semibold">Proof required before drafting</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {state.snapshot.proofRequirements.slice(0, 3).map((requirement) => (
                  <li key={requirement}>{requirement}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
